import {useState, useCallback, useEffect, useContext, useMemo} from "react";
import {AuthContext, useApi} from "@buildwitt/shapeshift-identity";
import {PageBaseContext} from "@/contexts/pageBaseContext";
import {debounce} from "@mui/material/utils";
import {IPaginatedResponse} from "@buildwitt/shapeshift-types";

export interface ICertificationOption {
	id: string;
	name: string;
	description?: string | null;
}

interface IPaginatedResponseWithNextPage<T> extends IPaginatedResponse<T> {
	hasNextPage: boolean;
}

const useGroupsCertifications = () => {
	const [certifications, setCertifications] = useState<{id: string; label: string}[]>([]);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [hasNextPage, setHasNextPage] = useState<boolean>(false);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [searchTerm, setSearchTerm] = useState<string | null>(null);
	const [apiResponse, submitApiCall] = useApi<IPaginatedResponseWithNextPage<ICertificationOption[]>>();
	const {jwt} = useContext(AuthContext);
	const [pageBaseState] = useContext(PageBaseContext);
	const cohortId = pageBaseState.ownedCohort && typeof pageBaseState.ownedCohort === "object" ? pageBaseState.ownedCohort.id : null;

	const fetchCertifications = useCallback((page: number, searchKey: string, append = false) => {
		if (!cohortId) {
			return;
		}

		submitApiCall({
			url: `/api/cohort/${cohortId}/groups/certifications`,
			method: "GET",
			config: {
				params: {
					page,
					pageSize: 20,
					...(searchKey ? {searchTerm: searchKey} : {})
				},
				headers: {
					"Authorization": `Bearer ${jwt}`,
					"Content-Type": "application/json"
				}
			},
			cache: true,
			throttle: !append
		});
	}, [submitApiCall, cohortId, jwt]);

	const handleSearchChange = useMemo(
		() => debounce((searchKey: string) => {
			setSearchTerm(searchKey);
			setCurrentPage(1);
			setCertifications([]);
			fetchCertifications(1, searchKey, false);
		}, 400),
		[fetchCertifications]
	);

	const loadMoreCertifications = useCallback(async () => {
		if (!hasNextPage || apiResponse.isLoading) return;
		const nextPage = currentPage + 1;
		setCurrentPage(nextPage);
		fetchCertifications(nextPage, searchTerm, true);
	}, [hasNextPage, apiResponse.isLoading, currentPage, searchTerm, fetchCertifications]);

	useEffect(() => {
		if (apiResponse.statusCode === 200 && apiResponse.data) {
			// Map API response to Certification format (name -> label)
			const mappedCertifications = apiResponse.data.data?.map((cert) => ({
				id: cert.id,
				label: cert.name
			})) || [];

			setCertifications(prev =>
				currentPage === 1 ? mappedCertifications : [...prev, ...mappedCertifications]
			);
			setTotalCount(apiResponse.data.totalCount);
			setHasNextPage(apiResponse.data.hasNextPage);
		} else if (apiResponse.statusCode && apiResponse.statusCode !== 200) {
			setCertifications([]);
			setTotalCount(0);
			setHasNextPage(false);
		}
	}, [apiResponse, currentPage]);

	return {
		certifications,
		totalCount,
		hasNextPage,
		loadMoreCertifications,
		apiResult: {
			isLoading: apiResponse.isLoading,
			isError: apiResponse.isError,
			error: apiResponse.errorData
		},
		handleSearchChange
	};
};

export default useGroupsCertifications;
